"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useIsCheckoutRoute } from "@/hooks/useIsCheckoutRoute";
import type { OneClickReorderStatus } from "@/hooks/useOneClickReorderFlow";
import { useNewCartStore } from "@/stores/useNewCartStore";
import { CartBody } from "./CartBody";
import { CartFooter } from "./CartFooter";
import { CartSidebarHeader } from "./CartSidebarHeader";
import { OrderSummary } from "./OrderSummary/OrderSummary";
import { ReorderFailed } from "./ReorderFailed";
import { ReorderPreparing } from "./ReorderPreparing";
interface Props {
  reorderStatus: OneClickReorderStatus;
  trustSignalsSlot?: React.ReactNode;
  optionalAddOnsSlot?: React.ReactNode;
}

/**
 * A reorder panel replaces the cart entirely; null means render the cart as usual.
 *
 * Only the terminal failure is dismissible. A reorder still in flight is still happening, so
 * reopening the cart has to keep showing it.
 */
function reorderPanel(status: OneClickReorderStatus, failureDismissed: boolean) {
  if (status === "preparing") return <ReorderPreparing />;
  if (status === "failed" && !failureDismissed) return <ReorderFailed />;
  return null;
}
export function CartSidebarContent(t0) {
  const $ = _c(22);
  const {
    reorderStatus,
    trustSignalsSlot,
    optionalAddOnsSlot
  } = t0;
  const isCartOpen = useNewCartStore(_temp);
  const setIsCartOpen = useNewCartStore(_temp2);
  const isCheckoutRoute = useIsCheckoutRoute();
  const autoOpenedForRef = useRef("idle");
  const [failureDismissed, setFailureDismissed] = useState(false);
  const [wasCartOpen, setWasCartOpen] = useState(isCartOpen);
  if (wasCartOpen !== isCartOpen) {
    setWasCartOpen(isCartOpen);
    if (!isCartOpen && reorderStatus === "failed") {
      setFailureDismissed(true);
    }
  }
  let t1;
  let t2;
  if ($[0] !== reorderStatus || $[1] !== setIsCartOpen) {
    t1 = () => {
      if (reorderStatus === "idle" || autoOpenedForRef.current === reorderStatus) {
        return;
      }
      autoOpenedForRef.current = reorderStatus;
      setFailureDismissed(false);
      setIsCartOpen(true);
    };
    t2 = [reorderStatus, setIsCartOpen];
    $[0] = reorderStatus;
    $[1] = setIsCartOpen;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== failureDismissed || $[5] !== reorderStatus) {
    t3 = reorderPanel(reorderStatus, failureDismissed);
    $[4] = failureDismissed;
    $[5] = reorderStatus;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const panel = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SheetTitle className="sr-only">Shopping Cart</SheetTitle>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isCheckoutRoute ? "Review and update your items." : "Review your items, apply a promo code, and continue to checkout.";
  let t6;
  if ($[8] !== t5) {
    t6 = <SheetDescription className="sr-only">{t5}</SheetDescription>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CartSidebarHeader />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== optionalAddOnsSlot || $[12] !== panel || $[13] !== trustSignalsSlot) {
    t8 = panel ?? <><CartBody trustSignalsSlot={trustSignalsSlot} optionalAddOnsSlot={optionalAddOnsSlot} /><OrderSummary /><CartFooter /></>;
    $[11] = optionalAddOnsSlot;
    $[12] = panel;
    $[13] = trustSignalsSlot;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t8) {
    t9 = <SheetContent side="right" className="w-[390px] p-0 sm:max-w-[390px] gap-0" showCloseButton={false}>{t4}{t6}{t7}{t8}</SheetContent>;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== isCartOpen || $[19] !== setIsCartOpen || $[20] !== t9) {
    t10 = <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>{t9}</Sheet>;
    $[18] = isCartOpen;
    $[19] = setIsCartOpen;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function _temp2(state_0) {
  return state_0.setIsCartOpen;
}
function _temp(state) {
  return state.isCartOpen;
}